import axiosInstance from '../config/axios.config';

/**
 * Backend-for-Frontend page aggregation (GH #162).
 *
 * One request returns everything a page needs, replacing the 3–9 call waterfall
 * these screens fired on open.
 *
 * WHAT A SECTION CONTAINS
 * -----------------------
 * `data.<section>` is exactly the payload the legacy endpoint's `data` key held.
 * That is deliberate and is covered by a contract test on both sides: callers
 * migrate by swapping where the payload comes from, not by rewriting how they
 * read it. Nothing downstream of the fetch needs to change.
 *
 * WHY THE ACCESSOR RE-WRAPS IN { success, data }
 * ----------------------------------------------
 * The existing loaders in these pages take a service response and branch on
 * `response.success`. Handing them back the same envelope means the migration
 * touches the fetch site only — the fifty lines of state-mapping that follow
 * stay untouched, and therefore cannot regress.
 */

/** @typedef {{ status: 'ok'|'error'|'skipped', http_status: number|null, replaces: string, message: string|null, extra?: object }} SectionMeta */

/**
 * @param {string} page   registry slug, e.g. 'lead-wizard'
 * @param {object} params query params (lead_id, product_id, per-section bags)
 * @param {object} [opts] { signal } — pass React Query's AbortSignal so
 *                        navigating away mid-flight cancels (#194).
 */
export const fetchPageData = async (page, params = {}, opts = {}) => {
    const res = await axiosInstance.get(`/page-data/${page}`, {
        params,
        signal: opts.signal,
    });

    if (!res.data?.success) {
        throw new Error(res.data?.message || `Failed to load ${page} page data`);
    }

    return wrap(res.data);
};

/**
 * Thin accessor over the raw aggregate response.
 *
 * Exported separately so tests and `usePageData` consumers can build one from a
 * fixture without going through the network.
 */
export const wrap = (body) => ({
    raw: body,

    /**
     * A section as its legacy `{ success, data }` envelope.
     *
     * A section that failed comes back `success: false` carrying the reason, so
     * a caller's existing error branch fires exactly as it did when that section
     * was its own request — one dead panel, not a dead page.
     */
    section(name) {
        const meta = body.meta?.sections?.[name];
        const ok = meta?.status === 'ok';

        return {
            success: ok,
            data: ok ? body.data?.[name] ?? null : null,
            message: ok ? null : meta?.message || `Section '${name}' unavailable`,
            ...(meta?.extra || {}),
        };
    },

    /** @returns {SectionMeta|undefined} */
    meta(name) {
        return body.meta?.sections?.[name];
    },

    /**
     * Facts the server worked out that the caller could not have known when it
     * built the request — notably `product_id` on the lead pages, which is the
     * whole reason those pages no longer need a round trip to learn it.
     *
     * A caller holding a seed must check this before applying product-scoped
     * sections: after the user switches product the seed still exists but now
     * describes the wrong one.
     */
    resolved(key) {
        const bag = body.meta?.resolved || {};
        return key === undefined ? bag : bag[key];
    },

    /** Section names that did not load, for a single consolidated warning. */
    failed() {
        return Object.entries(body.meta?.sections || {})
            .filter(([, m]) => m.status === 'error')
            .map(([name]) => name);
    },
});

export default fetchPageData;
